import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import CTASection from '../components/CTASection';
import { CheckCircle2, ArrowRight, Users, TrendingUp, Target, BarChart3 } from 'lucide-react';

const CRMSystemen = () => {
  const benefits = [
    {
      icon: <Users className="w-8 h-8 text-blue-700" />,
      title: 'Betere Klantrelaties',
      description: '360-graden inzicht in uw klanten en hun historie.',
    },
    {
      icon: <TrendingUp className="w-8 h-8 text-blue-700" />,
      title: 'Verhoogde Omzet',
      description: 'Meer deals sluiten door betere lead en opportunity tracking.',
    },
    {
      icon: <Target className="w-8 h-8 text-blue-700" />,
      title: 'Efficiëntere Processen',
      description: 'Automatiseer sales activiteiten en bespaar tijd.',
    },
    {
      icon: <BarChart3 className="w-8 h-8 text-blue-700" />,
      title: 'Data-Driven Beslissingen',
      description: 'Maak betere beslissingen op basis van realtime data.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <section className="relative pt-32 pb-20 overflow-hidden bg-gradient-to-br from-blue-50 via-white to-blue-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              CRM Systemen: Centraal Beheer van Klantrelaties
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Een CRM systeem (Customer Relationship Management) helpt bedrijven om klantrelaties te beheren, 
              sales te verhogen en processen te optimaliseren.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button 
                  size="lg" 
                  className="bg-blue-700 hover:bg-blue-800 text-white px-8 py-6 text-lg font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 group"
                >
                  Ontdek Uw CRM Oplossing
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Wat is een CRM Systeem?
            </h2>
            <div className="prose prose-lg text-gray-600 space-y-4">
              <p>
                Een CRM systeem is software die alle interacties met klanten en prospects centraliseert. Van eerste 
                contact tot after-sales service - alles wordt vastgelegd in één systeem dat toegankelijk is voor 
                uw hele team.
              </p>
              <p>
                Een goed CRM systeem helpt u om leads te verzamelen en kwalificeren, opportunities te tracken, 
                offertes te maken, contracten te beheren en klantcontacten bij te houden. Het geeft u realtime 
                inzicht in uw sales pipeline en helpt u om datagedreven beslissingen te nemen.
              </p>
              <p>
                Bij Guijs Consultancy specialiseren we ons in Zoho CRM, een van de meest gebruikte en krachtige 
                CRM systemen wereldwijd. Wij helpen Nederlandse bedrijven met de implementatie en optimalisatie 
                van hun CRM systeem.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Voordelen van een CRM Systeem
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Waarom investeren steeds meer bedrijven in een professioneel CRM systeem?
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <Card key={`item-${index}`} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300">
                <CardContent className="p-6">
                  <div className="mb-4">{benefit.icon}</div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Wanneer Heeft u een CRM Systeem Nodig?
            </h2>
            
            <div className="space-y-4 mt-8">
              {[
                'U wilt uw sales team effectiever laten werken',
                'Klantinformatie is verspreid over emails, Excel en notities',
                'U mist inzicht in uw sales pipeline en forecast',
                'Leads vallen tussen wal en schip door gebrek aan opvolging',
                'U wilt marketing en sales beter laten samenwerken',
                'Rapportages kosten veel tijd en zijn niet actueel',
                'Uw bedrijf groeit en processen worden onduidelijk',
                'U wilt klanten beter van dienst zijn met volledige historie',
              ].map((item, index) => (
                <div key={`item-${index}`} className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg">
                  <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700 text-lg">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Waarom Kiezen voor Zoho CRM?
            </h2>
            <p className="text-xl text-gray-600 mb-12">
              Zoho CRM is onze aanbevolen oplossing voor de meeste bedrijven. Hier is waarom:
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-left">
              <Card className="border-2 border-blue-200 shadow-lg">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Betaalbaar & Compleet</h3>
                  <p className="text-gray-600">
                    Alle functies die u nodig heeft tegen een eerlijke prijs. Ook voor MKB bedrijven.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-blue-200 shadow-lg">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Gebruiksvriendelijk</h3>
                  <p className="text-gray-600">
                    Intuïtieve interface waar uw team direct mee aan de slag kan.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-blue-200 shadow-lg">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Schaalbaar</h3>
                  <p className="text-gray-600">
                    Groeit mee van 5 tot 500+ gebruikers zonder prestatie verlies.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-blue-200 shadow-lg">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Integraties</h3>
                  <p className="text-gray-600">
                    Koppelt met 1000+ andere tools en systemen die u al gebruikt.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <CTASection 
        title="Klaar voor een Professioneel CRM Systeem?"
        subtitle="Laat ons u adviseren over de beste CRM oplossing voor uw bedrijf."
      />
    </div>
  );
};

export default CRMSystemen;
